<template>
  <article class="page page-orders page-order-details">
    <AppSpinner v-if="loading" />

    <template v-else>
      <section class="banner">
        <h1>Order Details</h1>

        <div>
          <AppLink
            :to="{ name: 'app_orders' }"
            class="btn btn-outline btn-secondary rounded-none"
          >
            View all orders
          </AppLink>

          <AppButton
            v-if="order.status === 'Awaiting Payment'"
            class="btn btn-solid bg-[#1C8644] hover:bg-green-600 text-white uppercase rounded-none"
            :class="loadingPayFast && '!bg-gray-light !text-gray-dark border border-gray-dark'"
            :processing="loadingPayFast"
            @click="payNow"
          >
            PAY NOW
          </AppButton>
        </div>
      </section>

      <section class="order-summary">
        <OrderItemHeader
          class="header"
          :item="order"
          :show-details-button="false"
          :show-edit-button="true"
          :show-icon="false"
          @edit-button-clicked="modalTypeToShow = 'status'"
        />

        <div
          class="summary-container"
          :class="order.deliveryFee !== 0 && !isEmpty(order.deliveryDetails) ? 'justify-between' : ''"
        >
          <template v-if="order.deliveryFee !== 0 && !isEmpty(order.deliveryDetails)">
            <AppUserAddress :data="order.deliveryDetails" />

            <hr>
          </template>

          <div class="summary">
            <OrderSummary :order="order" />
          </div>
        </div>

        <div class="px-6 pb-6 flex justify-between">
          <div>
            <AppLink
              v-if="order.hasPrintItems"
              :to="order.courierTrackingUrl ? order.courierTrackingUrl : ''"
              target="_blank"
              class="btn btn-secondary btn-solid rounded-none"
              :disabled="!order.courierTrackingUrl"
              :class="!order.courierTrackingUrl ? '!bg-gray-light cursor-default' : ''"
            >
              Track My Delivery
            </AppLink>

            <template v-if="authStore.user?.isAdmin && order.hasPrintItems">
              <AppButton
                class="ps-4 text-black"
                @click="modalTypeToShow = 'trackingUrl'"
              >
                Edit
              </AppButton>
            </template>
          </div>

          <div v-if="authStore.user.isAdmin">
            <AppButton
              class="btn btn-outline btn-secondary rounded-none"
              :disabled="submittingRegenerationRequest"
              :processing="submittingRegenerationRequest"
              @click="regenerateOrderItems()"
            >
              {{ submittingRegenerationRequest ? 'Regenerating order' : 'Regenerate Order' }}
            </AppButton>
          </div>
        </div>
      </section>

      <section class="order-items">
        <!--photos-->

        <ul class="items">
          <template
            v-for="item in order.items"
            :key="item.id"
          >
            <OrderDetailsItem
              :item="item"
              :order="order"
              :is-photo-pack="item.type === PhotoType.Pack"
            />
          </template>
        </ul>
      </section>
    </template>

    <!-- Edit Modal -->
    <Transition name="fade">
      <AppModal
        v-if="modalTypeToShow"
        :disabled="isSubmitting"
        class="event-overview-edit-modal"
        @close="modalTypeToShow = null"
      >
        <OrderForm
          :order="order"
          :modal-type="modalTypeToShow"
          :errors="errors"
          :processing="isSubmitting"
          @confirm="handleEditOrder"
          @close="modalTypeToShow = null"
        />
      </AppModal>
    </Transition>
  </article>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { ref } from 'vue';
import { useRoute } from 'vue-router';

import { AppButton, AppLink, AppModal, AppSpinner, AppUserAddress } from '@/components/app';
import { OrderForm } from '@/components/forms/admin';
import { OrderDetailsItem, OrderItemHeader, OrderSummary } from '@/components/orders';

import { useFormSubmit, useHttp } from '@/composables';
import config from '@/config';
import { PhotoType, ToastType } from '@/const';
import { useAuthStore, useToastStore } from '@/store';

const authStore = useAuthStore();

const { isSubmitting, errors } = useFormSubmit();

const order = ref({});
const loading = ref(true);
const submittingRegenerationRequest = ref(false);
const route = useRoute();

const modalTypeToShow = ref(null);

const toastStore = useToastStore();

const loadOrder = async () => {
  loading.value = true;

  const response = await useHttp().get(`/orders/${route.params.order}`);

  if (response.statusCode === HttpStatusCode.Ok) {
    order.value = response.data;
  }

  loading.value = false;
};

const refreshOrders = async () => {
  let attempts = 0;
  const initialMaxAttempts = 20;
  const initialInterval = 10000;
  let subsequentInterval = initialInterval;
  let missingDownloadableFile = null;

  const attemptLoad = async () => {
    // After the initial load, the API call will be made after 10 seconds at set intervals.
    // Only if the status or the items on the Order have changed will the actual order object be updated with the new data from the response,
    // this way the whole page is not refreshed by re-making the `loadOrder` call each time.
    if (attempts === 0) {
      await loadOrder();
    } else {
      const response = await useHttp().get(`/orders/${route.params.order}`);

      if (response.statusCode === HttpStatusCode.Ok) {
        if (response.data.status !== order.value.status || response.data.items !== order.value.items) {
          order.value = response.data;
        }
      }
    }

    attempts++;

    // Will return true if any of the items on the order are missing the downloadable file.
    missingDownloadableFile = order.value.items?.some(item => {
      return item.downloadableFile === null;
    });

    if (order.value.status === 'Complete' && !missingDownloadableFile) {
      return;
    }

    // The API call will happen at constant intervals before hitting the `initialMaxAttempts`, after that there will be a back-off`
    // for the time between each call is made, increasing exponentially.
    if (attempts < initialMaxAttempts) {
      setTimeout(attemptLoad, initialInterval);
    } else {
      subsequentInterval = subsequentInterval * 2;
      setTimeout(attemptLoad, subsequentInterval);
    }
  };

  await attemptLoad();
};

refreshOrders();

const loadingPayFast = ref(false);

const isEmpty = (obj) => {
  if (obj) {
    for (const prop in obj) {
      if (Object.hasOwn(obj, prop)) {
        return false;
      }
    }
  }

  return true;
};

const payNow = async () => {
  loadingPayFast.value = true;

  const paymentResponse = await useHttp().post('/payments', { 'order': order.value.id });

  if (paymentResponse.statusCode === HttpStatusCode.Ok) {
    const form = document.createElement('form');

    form.method = 'POST';
    form.action = config.payfastUrl;

    Object.entries(paymentResponse.data).forEach(([key, value]) => {
      const input = document.createElement('input');

      input.type = 'hidden';
      input.name = key;
      input.value = value;

      form.appendChild(input);
    });

    document.body.appendChild(form);

    form.submit();

    document.body.removeChild(form);
  } else {
    isSubmitting.value = false;
  }
};

// modal function
const handleEditOrder = async (formData) => {
  errors.value = null;
  isSubmitting.value = true;

  const response = await useHttp().put(`/orders/${order.value.id}`, formData);

  if (response.statusCode === HttpStatusCode.Ok) {
    await loadOrder();
  } else if (response.statusCode === HttpStatusCode.UnprocessableEntity) {
    errors.value = response.errors;
  }

  modalTypeToShow.value = null;
  isSubmitting.value = false;
};

const regenerateOrderItems = async () => {
  submittingRegenerationRequest.value = true;

  const response = await useHttp().post(`/orders/${order.value.id}/regenerate`);

  if (response.statusCode === HttpStatusCode.Ok) {
    toastStore.add(ToastType.Success, 'The order is being regenerated. Please check back in a few minutes.');
  } else {
    toastStore.add(ToastType.Error, 'There was an issue in regenerating the order. Please try again in a few minutes.');
  }

  submittingRegenerationRequest.value = false;
};

</script>
